import apiClient from "@/services/api-client";
import type { PlatformUser } from "@/types";

export type LoginPayload = {
  email: string;
  password: string;
};

export type AuthResponse = {
  accessToken: string;
  user: PlatformUser;
};

export type MfaChallengeResponse = {
  mfaRequired: true;
  challengeToken: string;
  challengeExpiresAt: string;
};

const authMutationConfig = () => ({
  headers: { "Idempotency-Key": crypto.randomUUID() },
});

export const authService = {
  login: async (payload: LoginPayload) => {
    const response = await apiClient.post<AuthResponse | MfaChallengeResponse>(
      "/auth/login",
      payload,
      authMutationConfig(),
    );
    return response.data;
  },
  completeMfaLogin: async (challengeToken: string, code: string) => {
    const response = await apiClient.post<AuthResponse>(
      "/auth/mfa/login",
      { challengeToken, code },
      authMutationConfig(),
    );
    return response.data;
  },
  refresh: async () => {
    const response = await apiClient.post<AuthResponse>("/auth/refresh", {});
    return response.data;
  },
  logout: async () => {
    await apiClient.post("/auth/logout", {});
  },
  stepUp: async (password: string) => {
    const response = await apiClient.post<{
      verifiedAt: string;
      expiresAt: string;
    }>("/auth/step-up", { password });
    return response.data;
  },
  me: async () => {
    const response = await apiClient.get<PlatformUser>("/auth/me");
    return response.data;
  },
  setupMfa: async (label?: string) => {
    const response = await apiClient.post<{
      secret: string;
      recoveryCodes: string[];
      otpauthUri: string;
    }>("/auth/mfa/setup", { label });
    return response.data;
  },
  verifyMfa: async (code: string) => {
    const response = await apiClient.post("/auth/mfa/verify", { code });
    return response.data;
  },
  disableMfa: async (code: string) => {
    const response = await apiClient.post("/auth/mfa/disable", { code });
    return response.data;
  },
};
